<template>
    <div class="checklist-view">
        <!-- Loading state -->
        <div v-if="loading" class="loading-container">
            <div class="spinner"></div>
            <p>Carregando detalhes do checklist...</p>
        </div>

        <!-- Main content -->
        <div v-else-if="checklist.id" class="checklist-container">
            <!-- Header Section -->
            <div class="checklist-header">
                <div class="header-top">
                    <h1>Detalhes do Checklist</h1>
                    <div class="status-badge" :class="getStatusClass(checklist.status)">
                        {{ checklist.status }}
                    </div>
                </div>
                
                <div class="header-info">
                    <div class="info-grid">
                        <div class="info-item">
                            <label>ID:</label>
                            <span>{{ checklist.id }}</span>
                        </div>
                        <div class="info-item">
                            <label>Nome:</label>
                            <span>{{ checklist.name }}</span>
                        </div>
                        <div class="info-item">
                            <label>Unidade:</label>
                            <span>{{ checklist.branch_name }}</span>
                        </div>
                        <div class="info-item">
                            <label>Equipe:</label>
                            <span>{{ checklist.team_name }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Details Section -->
            <div class="checklist-details">
                <h2>Informações Detalhadas</h2>
                
                <div class="details-grid">
                    <div class="detail-card">
                        <h3>Período de Execução</h3>
                        <div class="detail-content">
                            <div class="detail-row">
                                <span class="label">Início:</span>
                                <span>{{ formatDateTime(checklist.started_at) }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="label">Fim:</span>
                                <span>{{ formatDateTime(checklist.finished_at) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="detail-card" v-if="checklist.starter">
                        <h3>Responsáveis</h3>
                        <div class="detail-content">
                            <div class="detail-row">
                                <span class="label">Iniciado por:</span>
                                <span>{{ checklist.starter.name }}</span>
                            </div>
                            <div class="detail-row" v-if="checklist.finisher">
                                <span class="label">Finalizado por:</span>
                                <span>{{ checklist.finisher.name }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="detail-card" v-if="checklist.team">
                        <h3>Equipe</h3>
                        <div class="detail-content">
                            <div class="detail-row">
                                <span class="label">Nome:</span>
                                <span>{{ checklist.team.name }}</span>
                            </div>
                            <div class="detail-row">
                                <span class="label">Membros:</span>
                                <span>{{ checklist.team.user_count }} colaboradores</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Items Section -->
            <div class="checklist-items">
                <h2>Itens do Checklist</h2>
                
                <div class="items-list">
                    <div 
                        v-for="(item, index) in checklist.items" 
                        :key="item.id" 
                        class="item-card"
                        :class="getItemStatusClass(item.status)"
                    >
                        <div class="item-header">
                            <div class="item-number">{{ index + 1 }}</div>
                            <h3>{{ item.title }}</h3>
                            <div class="item-status" :class="getItemStatusClass(item.status)">
                                {{ item.status }}
                            </div>
                        </div>
                        
                        <div class="item-content">
                            <p class="item-description">{{ item.description }}</p>
                            
                            <div v-if="item.obs" class="item-obs">
                                <strong>Observações:</strong>
                                <p>{{ item.obs }}</p>
                            </div>
                            
                            <div v-if="item.document" class="item-document">
                                <strong>Documento:</strong>
                                <a :href="item.document" target="_blank" class="document-link">
                                    Ver documento
                                </a>
                            </div>
                            
                            <div v-if="item.nonconformities && item.nonconformities.length > 0" class="item-nonconformities">
                                <strong>Não Conformidades:</strong>
                                <ul>
                                    <li v-for="nc in item.nonconformities" :key="nc.id">
                                        {{ nc.description }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer Actions -->
            <div class="checklist-footer">
                <button @click="goBack" class="btn btn-secondary">
                    ← Voltar
                </button>
                <button @click="exportPdf" class="btn btn-primary">
                    📄 Exportar PDF
                </button>
            </div>
        </div>

        <!-- Error state -->
        <div v-else class="error-container">
            <h2>Checklist não encontrado</h2>
            <p>Não foi possível carregar os detalhes do checklist.</p>
            <button @click="goBack" class="btn btn-secondary">Voltar</button>
        </div>
    </div>
</template>

<script>
import BaseCrud from '../Base/BaseCrud'

export default {
    data: function () {
        return {
            checklist: {},
            loading: true
        }
    },
    methods: {
        formatDateTime(dateTime) {
            if (!dateTime) return '-';
            
            const date = new Date(dateTime);
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        
        getStatusClass(status) {
            if (!status) return 'status-default';
            switch (status.toLowerCase()) {
                case 'completo':
                    return 'status-complete';
                case 'em andamento':
                    return 'status-progress';
                case 'pendente':
                    return 'status-pending';
                default:
                    return 'status-default';
            }
        },
        
        getItemStatusClass(status) {
            if (!status) return 'item-default';
            switch (status.toLowerCase()) {
                case 'ok':
                    return 'item-ok';
                case 'nok':
                    return 'item-nok';
                case 'na':
                    return 'item-na';
                default:
                    return 'item-default';
            }
        },
        
        goBack() {
            this.$router.go(-1);
        },
        
        exportPdf() {
            if (!this.checklist.id) {
                alert('Erro: Checklist não encontrado');
                return;
            }

            // Mostrar loading
            const originalText = event.target.textContent;
            event.target.textContent = '📄 Gerando PDF...';
            event.target.disabled = true;

            // Fazer download do PDF
            this.$http.get(this.$store.state.api + 'checklists/' + this.checklist.id + '/export-pdf', {
                responseType: 'blob'
            })
            .then(response => {
                // Criar URL para o blob
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                
                // Criar link temporário para download
                const link = document.createElement('a');
                link.href = url;
                link.download = `checklist_${this.checklist.name}_${new Date().getTime()}.pdf`;
                document.body.appendChild(link);
                link.click();
                
                // Limpar
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                
                // Restaurar botão
                event.target.textContent = originalText;
                event.target.disabled = false;
            })
            .catch(error => {
                console.error('Erro ao gerar PDF:', error);
                alert('Erro ao gerar PDF. Tente novamente.');
                
                // Restaurar botão
                event.target.textContent = originalText;
                event.target.disabled = false;
            });
        }
    },
    components: {
        BaseCrud
    },
    mounted() {
        console.log(this.$route.params.id);

        this.$http.get(this.$store.state.api + 'checklists-execution-by-id/' + this.$route.params.id + '?with[]=items&with[]=template&with[]=starter&with[]=finisher')
            .then(response => {
                this.checklist = response.data;
                this.loading = false;
            })
            .catch(error => {
                console.log(error);
                this.loading = false;
            });
    }
}
</script>

<style scoped>
.checklist-view {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

/* Loading */
.loading-container {
    text-align: center;
    padding: 60px 20px;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

/* Header */
.checklist-header {
    background: white;
    border-radius: 12px;
    padding: 30px;
    margin-bottom: 25px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
}

.header-top h1 {
    margin: 0;
    color: #333;
    font-size: 28px;
    font-weight: 600;
}

.status-badge {
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 14px;
    text-transform: uppercase;
}

.status-complete {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status-progress {
    background: #fff3cd;
    color: #856404;
    border: 1px solid #ffeaa7;
}

.status-pending {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.info-item label {
    font-weight: 600;
    color: #666;
    font-size: 14px;
}

.info-item span {
    font-size: 16px;
    color: #333;
}

/* Details */
.checklist-details {
    background: white;
    border-radius: 12px;
    padding: 30px;
    margin-bottom: 25px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.checklist-details h2 {
    margin: 0 0 25px 0;
    color: #333;
    font-size: 22px;
    font-weight: 600;
}

.details-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
}

.detail-card {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    border: 1px solid #e9ecef;
}

.detail-card h3 {
    margin: 0 0 15px 0;
    color: #495057;
    font-size: 16px;
    font-weight: 600;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
}

.detail-row:last-child {
    margin-bottom: 0;
}

.detail-row .label {
    font-weight: 500;
    color: #666;
}

/* Items */
.checklist-items {
    background: white;
    border-radius: 12px;
    padding: 30px;
    margin-bottom: 25px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.checklist-items h2 {
    margin: 0 0 25px 0;
    color: #333;
    font-size: 22px;
    font-weight: 600;
}

.items-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.item-card {
    border: 2px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    transition: all 0.3s ease;
}

.item-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.item-ok {
    border-left: 4px solid #28a745;
    background: #f8fff9;
}

.item-nok {
    border-left: 4px solid #dc3545;
    background: #fff8f8;
}

.item-na {
    border-left: 4px solid #6c757d;
    background: #f8f9fa;
}

.item-header {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    gap: 15px;
}

.item-number {
    background: #007bff;
    color: white;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
    flex-shrink: 0;
}

.item-header h3 {
    margin: 0;
    flex: 1;
    color: #333;
    font-size: 18px;
    font-weight: 600;
}

.item-status {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.item-status.item-ok {
    background: #d4edda;
    color: #155724;
}

.item-status.item-nok {
    background: #f8d7da;
    color: #721c24;
}

.item-status.item-na {
    background: #e2e3e5;
    color: #383d41;
}

.item-description {
    color: #666;
    margin-bottom: 15px;
    line-height: 1.5;
}

.item-obs, .item-document, .item-nonconformities {
    margin-top: 15px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
    border-left: 3px solid #007bff;
}

.item-obs strong, .item-document strong, .item-nonconformities strong {
    color: #495057;
    display: block;
    margin-bottom: 8px;
}

.document-link {
    color: #007bff;
    text-decoration: none;
    font-weight: 500;
}

.document-link:hover {
    text-decoration: underline;
}

.item-nonconformities ul {
    margin: 0;
    padding-left: 20px;
}

.item-nonconformities li {
    margin-bottom: 5px;
}

/* Footer */
.checklist-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 0;
}

.btn {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
}

.btn-primary {
    background: #007bff;
    color: white;
}

.btn-primary:hover {
    background: #0056b3;
    transform: translateY(-1px);
}

.btn-secondary {
    background: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background: #545b62;
    transform: translateY(-1px);
}

/* Error */
.error-container {
    text-align: center;
    padding: 60px 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.error-container h2 {
    color: #dc3545;
    margin-bottom: 15px;
}

.error-container p {
    color: #666;
    margin-bottom: 25px;
}

/* Responsive */
@media (max-width: 768px) {
    .checklist-view {
        padding: 15px;
    }
    
    .header-top {
        flex-direction: column;
        gap: 15px;
        align-items: flex-start;
    }
    
    .info-grid {
        grid-template-columns: 1fr;
    }
    
    .details-grid {
        grid-template-columns: 1fr;
    }
    
    .item-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
    
    .checklist-footer {
        flex-direction: column;
        gap: 15px;
    }
}

.VuePagination {
    text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/CheckList/ViewExecuted.vue